"use client";

import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { FC, useContext, useMemo, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import { ModalsContext } from "@/context/ModalsContext";
import { CustomLink, VideoModal } from "@/types";

import Icon from "../ui/Icon/Icon";

const Modal = dynamic(() => import("@/components/ui/Modal/Modal"), { ssr: false });
const ModalContent = dynamic(() => import("@/components/ui/Modal/ModalContent"), { ssr: false });

export interface TextBaselineProps {
  entry: {
    projectButton: CustomLink;
    brochureButton: CustomLink;
    videoButton: VideoModal;
  };
}

export const PropertyType3Buttons: FC<TextBaselineProps> = ({ entry }) => {
  const locale = useLocale();
  const t = useTranslations();
  const { setDownloadBrochureModalOpen } = useContext(ModalsContext);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  return (
    <div className="flex justify-center gap-m">
      {entry.projectButton && (
        <Button
          href={getLink(entry.projectButton, locale)}
          size={ButtonSize.SMALL}
          theme={ButtonTheme.BLACK}
          type="button"
          variant={ButtonVariant.PRIMARY}
          customClass="inline-flex"
        >
          <span className="flex items-center uppercase  font-medium">
            {entry.projectButton.link.icon && <Icon className="stroke-black" name={entry.projectButton.link.icon} size="20" />}
            {entry.projectButton.label}
          </span>
        </Button>
      )}
      {entry.brochureButton && (
        <Button
          size={ButtonSize.SMALL}
          theme={ButtonTheme.WHITE}
          type="button"
          variant={ButtonVariant.PRIMARY}
          onClick={() => setDownloadBrochureModalOpen(true)}
        >
          <span className="flex items-center uppercase gap-xs font-medium">
            {entry.brochureButton.link.icon && <Icon className="stroke-inherit" name={entry.brochureButton.link.icon} size="20" />}
            {entry.brochureButton.label}
          </span>
        </Button>
      )}
      {entry.videoButton && (
        <>
          <Button
            size={ButtonSize.SMALL}
            theme={ButtonTheme.WHITE}
            type="button"
            variant={ButtonVariant.PRIMARY}
            onClick={() => setVideoModalOpen(true)}
          >
            <span className="flex items-center uppercase gap-xs font-medium">
              <Icon className="stroke-inherit" name="play" size="20" />
              {entry.videoButton.buttonLabel}
            </span>
          </Button>
          <Modal open={videoModalOpen} onClose={() => setVideoModalOpen(false)}>
            <ModalContent className="h-dvh w-dvw !rounded-none pt-m">
              <div className="flex items-center justify-between mb-l">
                <h2 className="font-primary uppercase">{entry.videoButton.buttonLabel}</h2>
                <Button
                  className="stroke-inherit"
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.TERTIARY}
                  theme={ButtonTheme.BLACK}
                  onClick={() => setVideoModalOpen(false)}
                >
                  <span className="pointer-events-none">
                    <Icon name="close" size="24" title="close" className="stroke-black" />
                    <span className="sr-only">{t("button.label.close")}</span>
                  </span>
                </Button>
              </div>
              {entry.videoButton.embeddedVideo ? (
                <iframe
                  className="object-cover w-full h-[calc(100dvh-82px)] rounded-lg border-0"
                  width="560"
                  height="315"
                  src={entry.videoButton.embeddedVideo.link.url}
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                ></iframe>
              ) : (
                <video
                  id="video-player"
                  className="object-cover w-full max-h-[calc(100dvh-82px)] rounded-lg"
                  src={entry.videoButton.video?.url}
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src={entry.videoButton.video?.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </ModalContent>
          </Modal>
        </>
      )}
    </div>
  );
};

export default PropertyType3Buttons;
